import React, { useCallback, useEffect, useRef, useState } from "react";
import "./range-slider.scss";

export type TRangeSlider = {
    label: string;
    min: number;
    max: number;
    rangeValue?: { min: number; max: number };
    singleValue?: number;
    onRangeChange?: ({ min, max }: { min: number; max: number }) => void;
    onSingleChange?: (value: number) => void;
    ariaLabelMin?: string;
    ariaLabelMax?: string;
    hideLabel?: boolean;
    ariaLabelledby?: string;
    unit?: string;
    mode?: "single" | "range";
    step?: number;
};

export const RangeSlider = ({
    label,
    min,
    max,
    rangeValue,
    singleValue,
    onRangeChange,
    onSingleChange,
    ariaLabelMin = "Minimum value",
    ariaLabelMax = "Maximum value",
    hideLabel = false,
    unit = "",
    ariaLabelledby,
    mode = "range",
    step = 1,
}: TRangeSlider) => {
    const [minVal, setMinVal] = useState(rangeValue?.min ?? 0);
    const [maxVal, setMaxVal] = useState(rangeValue?.max ?? 0);
    const minValRef = useRef(rangeValue?.min ?? 0);
    const maxValRef = useRef(rangeValue?.max ?? 0);
    const [singleVal, setSingleVal] = useState(singleValue ?? 0);
    const rangeRef = useRef<HTMLDivElement>(null);
    const isRange = mode === "range";

    // Update slider values accordingly if min or max is changed
    useEffect(() => {
        const newMin = Math.max(minValRef.current ?? 0, min);
        const newMax = Math.min(maxValRef.current ?? 0, max);

        setMinVal(newMin);
        setMaxVal(newMax);

        minValRef.current = newMin;
        maxValRef.current = newMax;
        // eslint-disable-next-line
    }, [min, max]);

    useEffect(() => {
        setSingleVal(singleValue ?? 0);
    }, [singleValue]);

    // Convert to percentage
    const getPercent = useCallback((value: number) => Math.round(((value - min) / (max - min)) * 100), [min, max]);

    // Calculate percentage fulfilled based on current value relative to max
    const getSinglePercent = useCallback(
        (value: number) => {
            return Math.min(100, Math.max(0, (value / max) * 100));
        },
        [max]
    );

    // Set width of the range to decrease from the left side
    useEffect(() => {
        if (isRange) {
            const minPercent = getPercent(minVal);
            const maxPercent = getPercent(maxValRef.current);

            if (rangeRef.current) {
                rangeRef.current.style.left = `${minPercent}%`;
                rangeRef.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [minVal, getPercent, isRange]);

    // Set width of the range to decrease from the right side
    useEffect(() => {
        if (isRange) {
            const minPercent = getPercent(minValRef.current);
            const maxPercent = getPercent(maxVal);

            if (rangeRef.current) {
                rangeRef.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [maxVal, getPercent, isRange]);

    // Set width of the range when in single mode
    useEffect(() => {
        if (!isRange) {
            const percentFulfilled = getSinglePercent(singleVal);

            if (rangeRef.current) {
                rangeRef.current.style.left = "0";
                rangeRef.current.style.width = `${percentFulfilled}%`;
            }
        }
    }, [singleVal, getSinglePercent, isRange]);

    useEffect(() => {
        if (isRange) {
            onRangeChange?.({ min: minVal, max: maxVal });
        }
        // eslint-disable-next-line
    }, [minVal, maxVal, isRange]);

    useEffect(() => {
        if (!isRange) {
            onSingleChange?.(singleVal);
        }
        // eslint-disable-next-line
    }, [singleVal, isRange]);

    return (
        <>
            {!hideLabel && <label>{label}</label>}
            <div className="c-slider">
                <div
                    className="c-slider__slider"
                    role="group"
                    aria-label={hideLabel && !ariaLabelledby ? label : undefined}
                    aria-labelledby={ariaLabelledby}
                >
                    {mode === "range" && (
                        <>
                            <input
                                type="range"
                                min={min}
                                max={max}
                                value={minVal}
                                onChange={e => {
                                    const value = Math.min(Number(e.target.value), maxVal - step);
                                    setMinVal(value);
                                    minValRef.current = value;
                                }}
                                className="c-slider__input c-slider__input--left"
                                aria-label={ariaLabelMin ?? label}
                                aria-valuemin={min}
                                aria-valuemax={max}
                                aria-valuenow={minVal}
                                aria-valuetext={`${minVal} - ${maxVal} ${unit}`}
                                step={step}
                            />
                            <input
                                type="range"
                                min={min}
                                max={max}
                                value={maxVal}
                                onChange={e => {
                                    const value = Math.max(Number(e.target.value), minVal + step);
                                    setMaxVal(value);
                                    maxValRef.current = value;
                                }}
                                className="c-slider__input c-slider__input--right"
                                aria-label={ariaLabelMax ?? label}
                                aria-valuemin={min}
                                aria-valuemax={max}
                                aria-valuenow={maxVal}
                                aria-valuetext={`${minVal} - ${maxVal} ${unit}`}
                                step={step}
                            />
                        </>
                    )}
                    {mode === "single" && (
                        <input
                            type="range"
                            min={min}
                            max={max}
                            value={singleValue}
                            onChange={e => setSingleVal(Number(e.target.value))}
                            className="c-slider__input"
                            aria-label={label}
                            aria-valuemin={min}
                            aria-valuemax={max}
                            aria-valuenow={singleValue}
                            aria-valuetext={`${singleValue} ${unit}`}
                            step={step}
                        />
                    )}
                    <div className="c-slider__track" />
                    <div ref={rangeRef} className="c-slider__range" aria-hidden="true" />
                </div>
            </div>
        </>
    );
};
